import { createContext, useContext, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

import { ApiError, get, post } from "./api";
import { currentSurface } from "./host";
import type { User } from "./types";

/**
 * Session state.
 *
 * The session itself is an HttpOnly cookie the client cannot read, so "am I
 * logged in" is answered by asking the API rather than by inspecting storage.
 */
interface AuthState {
  user: User | null;
  loading: boolean;
  /** The session check itself failed (API down, proxy error): not "logged out". */
  unreachable: boolean;
  retry: () => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loginError: string | null;
  loggingIn: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

/** Endpoints differ per surface; the host decides which pair applies. */
function endpoints() {
  return currentSurface() === "operator"
    ? { me: "/api/operator/me", login: "/api/operator/login", logout: "/api/operator/logout" }
    : { me: "/api/me", login: "/api/login", logout: "/api/logout" };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { me, login, logout } = endpoints();

  // The guest's claim page and the PIN-only scanners never use a panel
  // session; asking for one there costs a guest on club Wi-Fi a round trip
  // and logs a 401 for nothing.
  const { pathname } = useLocation();
  const panel = !/^\/(claim\/|scanner$|rewards$)/.test(pathname);

  const session = useQuery({
    enabled: panel,
    queryKey: ["session"],
    queryFn: async () => {
      try {
        const data = await get<{ user: User }>(me);
        return data.user;
      } catch (error) {
        // A 401 is the normal "not logged in" answer, not a failure worth
        // retrying or surfacing as an error state.
        if (error instanceof ApiError && error.isUnauthenticated) return null;
        throw error;
      }
    },
    // A 401 is final; an outage gets two quick retries, then the shell shows
    // "unreachable" rather than a login form that would also fail.
    retry: (count, error) => !(error instanceof ApiError && error.isUnauthenticated) && count < 2,
    retryDelay: 800,
    staleTime: 60_000,
  });

  const loginMutation = useMutation({
    mutationFn: (credentials: { email: string; password: string }) =>
      post<{ user: User }>(login, credentials),
    onSuccess: (data) => {
      queryClient.setQueryData(["session"], data.user);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => post<{ ok: boolean }>(logout),
    onSuccess: () => {
      queryClient.setQueryData(["session"], null);
      // Everything cached belonged to that session.
      queryClient.clear();
    },
  });

  const value: AuthState = {
    user: session.data ?? null,
    loading: session.isLoading,
    unreachable: session.isError,
    retry: () => void session.refetch(),
    loginError: loginMutation.error instanceof Error ? loginMutation.error.message : null,
    loggingIn: loginMutation.isPending,
    login: async (email, password) => {
      await loginMutation.mutateAsync({ email, password });
    },
    logout: async () => {
      await logoutMutation.mutateAsync();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
